import { KeyValue, KeyValuePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { ValidationErrors } from '@angular/forms';

import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { PasswordRules } from '@connect-services/client/sdk/api';
import { SecurityService } from '@connect-services/client/services/security.service';
import {
    ConfirmedPasswordFormGroup,
    ForgotPasswordFormGroup,
    FormErrorEnum,
    LoginFormGroup,
    SecurityFormField,
    SsoLoginFormGroup,
} from '@shared/client/models/form.model';
import { FormService } from '@shared/client/services/form.service';

export type AuthFormType = ConfirmedPasswordFormGroup | ForgotPasswordFormGroup | LoginFormGroup | SsoLoginFormGroup;

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-security-base',
    standalone: false, // eslint-disable-line @angular-eslint/prefer-standalone
    template: './security-base.component.html',
})
export abstract class AbstractSecurityComponent implements OnInit {
    public actionLabel!: string;
    public fieldNames!: SecurityFormField[];
    public readonly formGroup!: WritableSignal<AuthFormType>;
    public readonly formService = inject(FormService);
    public hasBackNavigationButton = false;
    public isConfirmPasswordVisible = false;
    public isDontHaveAnAccountHelpDisplayed = false;
    public readonly isLoadingSSO = signal<boolean>(false);
    public isLogin = false;
    public isPasswordVisible = false;
    public isSubmitting = false;
    public title!: string;

    protected readonly isLoading = signal<boolean>(false);
    protected organizationPasswordRules: PasswordRules | null = null;
    protected passwordRulesErrorKeys: string[] = [
        FormErrorEnum.MIN_LENGTH,
        FormErrorEnum.MISSING_SPECIAL_CHARACTER,
        FormErrorEnum.MISSING_NUMBER,
        FormErrorEnum.MISSING_UPPERCASE_CHARACTER,
    ];
    // eslint-disable-next-line @typescript-eslint/member-ordering
    protected readonly FormErrorEnum = FormErrorEnum;
    protected readonly RoutePathEnum = RoutePathEnum;
    protected readonly SecurityFormField = SecurityFormField;
    protected readonly securityService = inject(SecurityService);

    private readonly keyValuePipe = inject(KeyValuePipe);

    public getInputType(fieldName: SecurityFormField): 'text' | 'password' {
        switch (fieldName) {
            case SecurityFormField.EMAIL:
            case SecurityFormField.IDENTIFIER:
                return 'text';
            case SecurityFormField.CONFIRM_PASSWORD:
            case SecurityFormField.PASSWORD:
            default:
                return this.isVisible(fieldName) ? 'text' : 'password';
        }
    }

    public getPrefixIcon(fieldName: SecurityFormField): 'user' | 'lock' {
        switch (fieldName) {
            case SecurityFormField.EMAIL:
            case SecurityFormField.IDENTIFIER:
                return 'user';
            case SecurityFormField.CONFIRM_PASSWORD:
            case SecurityFormField.PASSWORD:
            default:
                return 'lock';
        }
    }

    public getSuffixIcon(fieldName: SecurityFormField): 'eye' | 'eye-slash' {
        return this.isVisible(fieldName) ? 'eye' : 'eye-slash';
    }

    public async ngOnInit(): Promise<void> {
        this.isLoading.set(true);
        try {
            await this.preInit();
        } catch {
            this.isLoading.set(false);

            return;
        }

        this.formGroup.set(this.initForm());
        this.isLoading.set(false);
    }

    public async submitForm(): Promise<void> {
        if (this.formGroup().invalid) {
            return;
        }

        this.isSubmitting = true;
        try {
            await this.submitAction();
        } finally {
            this.isSubmitting = false;
        }
    }

    public togglePasswordVisibility(fieldName: SecurityFormField): void {
        if (fieldName === SecurityFormField.CONFIRM_PASSWORD) {
            this.isConfirmPasswordVisible = !this.isConfirmPasswordVisible;

            return;
        }

        this.isPasswordVisible = !this.isPasswordVisible;
    }

    protected getControlErrors(fieldName: string): KeyValue<string, ValidationErrors>[] {
        const errors = this.keyValuePipe.transform<string, ValidationErrors>(
            this.formGroup().controls[fieldName].errors,
        );

        if (!errors) {
            return [];
        }

        return errors.filter(
            (error: KeyValue<string, ValidationErrors>) => !this.passwordRulesErrorKeys.includes(error.key),
        );
    }

    private isVisible(fieldName: SecurityFormField): boolean {
        return fieldName === SecurityFormField.CONFIRM_PASSWORD
            ? this.isConfirmPasswordVisible
            : this.isPasswordVisible;
    }

    protected abstract initForm(): AuthFormType;
    protected abstract preInit(): void | Promise<void>;
    protected abstract submitAction(): Promise<void>;
}
